import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { navItemsForRole } from '@/components/nav/nav-items';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const items = user ? navItemsForRole(user.role) : [];

  // Keyboard-operable equivalent of the backdrop click (Esc closes the mobile drawer) —
  // 10-design-system.md §5 keyboard operability baseline.
  useEffect(() => {
    if (!isOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {isOpen && <div className="fa-sidebar__backdrop" onClick={onClose} aria-hidden="true" />}
      <nav
        className={`fa-sidebar ${isOpen ? 'fa-sidebar--open' : ''}`}
        aria-label="Main navigation"
      >
        <ul className="fa-sidebar__list">
          {items.map((item) => (
            <li key={item.key}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `fa-sidebar__link ${isActive ? 'fa-sidebar__link--active' : ''}`
                }
                onClick={onClose}
              >
                <span className="fa-sidebar__icon" aria-hidden="true">
                  <item.icon />
                </span>
                <span className="fa-sidebar__label">{t(item.labelKey)}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}
